/**
 * Hook für API Storage - Ersetzt useSharePointStorage
 */

import { useCallback, useMemo } from 'react';
import { APIStorage } from '../services/apiStorage';
import type {
  CompanySettings,
  DayEntry,
  VacationCarryOver,
  Department,
  EmploymentType,
  WageType,
  CreateUserData,
  ImportAzureUserData,
  VacationAdjustmentCategory,
  CreateHistoryRequest,
} from '../types';

export function useAPIStorage() {
  // Erstelle APIStorage Instanz (Token wird aus localStorage geladen)
  const storage = useMemo(() => {
    return new APIStorage();
  }, []);

  // ===== Current User =====

  const getMe = useCallback(async () => {
    return await storage.getMe();
  }, [storage]);

  // ===== Settings =====

  const getSettings = useCallback(async () => {
    return await storage.getSettings();
  }, [storage]);

  const saveSettings = useCallback(
    async (settings: CompanySettings) => {
      await storage.saveSettings(settings);
    },
    [storage]
  );

  const getUserSettings = useCallback(
    async (userId: string) => {
      return await storage.getUserSettings(userId);
    },
    [storage]
  );

  const saveUserSettings = useCallback(
    async (userId: string, settings: CompanySettings) => {
      await storage.saveUserSettings(userId, settings);
    },
    [storage]
  );

  // ===== Entries =====

  const getEntries = useCallback(
    async (year: number) => {
      return await storage.getEntries(year);
    },
    [storage]
  );

  const saveEntries = useCallback(
    async (year: number, entries: Record<string, DayEntry>) => {
      await storage.saveEntries(year, entries);
    },
    [storage]
  );

  const getUserEntries = useCallback(
    async (userId: string, year: number) => {
      return await storage.getUserEntries(userId, year);
    },
    [storage]
  );

  const saveUserEntries = useCallback(
    async (userId: string, year: number, entries: Record<string, DayEntry>) => {
      await storage.saveUserEntries(userId, year, entries);
    },
    [storage]
  );

  const addManagerNote = useCallback(
    async (userId: string, date: string, note: string) => {
      await storage.addManagerNote(userId, date, note);
    },
    [storage]
  );

  // ===== Carryover =====

  const getCarryOver = useCallback(
    async (year: number) => {
      return await storage.getCarryOver(year);
    },
    [storage]
  );

  const saveCarryOver = useCallback(
    async (year: number, carryOver: VacationCarryOver) => {
      await storage.saveCarryOver(year, carryOver);
    },
    [storage]
  );

  const getUserCarryOver = useCallback(
    async (userId: string, year: number) => {
      return await storage.getUserCarryOver(userId, year);
    },
    [storage]
  );

  const saveUserCarryOver = useCallback(
    async (userId: string, year: number, carryOver: VacationCarryOver) => {
      await storage.saveUserCarryOver(userId, year, carryOver);
    },
    [storage]
  );

  const autoCalculateCarryover = useCallback(
    async (year: number, userId?: string, force?: boolean) => {
      return await storage.autoCalculateCarryover(year, userId, force);
    },
    [storage]
  );

  // ===== Users =====

  const getAllUsers = useCallback(async () => {
    return await storage.getAllUsers();
  }, [storage]);

  const getUsersByDepartment = useCallback(
    async (department: Department) => {
      return await storage.getUsersByDepartment(department);
    },
    [storage]
  );

  const getUserData = useCallback(
    async (userId: string, year: number) => {
      return await storage.getUserData(userId, year);
    },
    [storage]
  );

  const updateUserDepartment = useCallback(
    async (userId: string, department: Department) => {
      await storage.updateUserDepartment(userId, department);
    },
    [storage]
  );

  const updateUser = useCallback(
    async (userId: string, data: {
      personnelNumber?: string | null;
      department?: Department | null;
      employmentType?: EmploymentType | null;
      employmentStart?: string | null;
      employmentEnd?: string | null;
      excludeFromExport?: boolean;
      rv?: boolean;
      hansefit?: boolean;
      contractualHours?: number | null;
      wageType?: WageType | null;
      monthlySalary?: number | null;
      firstName?: string;
      lastName?: string;
    }) => {
      await storage.updateUser(userId, data);
    },
    [storage]
  );

  const updateUserEmail = useCallback(
    async (userId: string, email: string) => {
      return await storage.updateUserEmail(userId, email);
    },
    [storage]
  );

  // ===== User Create =====

  const createUser = useCallback(
    async (data: CreateUserData) => {
      return await storage.createUser(data);
    },
    [storage]
  );

  const searchAzureUsers = useCallback(
    async (query: string) => {
      return await storage.searchAzureUsers(query);
    },
    [storage]
  );

  const importAzureUser = useCallback(
    async (data: ImportAzureUserData) => {
      return await storage.importAzureUser(data);
    },
    [storage]
  );

  // ===== Approvals =====

  const getApprovals = useCallback(
    async (year: number, month: number) => {
      return await storage.getApprovals(year, month);
    },
    [storage]
  );

  const setApproval = useCallback(
    async (userId: string, year: number, month: number, data: { approved: boolean; approvalNote?: string | null }) => {
      return await storage.setApproval(userId, year, month, data);
    },
    [storage]
  );

  // ===== Personnel Number =====

  const updatePersonnelNumber = useCallback(
    async (userId: string, personnelNumber: string | null) => {
      return await storage.updatePersonnelNumber(userId, personnelNumber);
    },
    [storage]
  );

  // ===== User Delete =====

  const deleteUser = useCallback(
    async (userId: string) => {
      await storage.deleteUser(userId);
    },
    [storage]
  );

  // ===== Manager Assignments =====

  const getManagerAssignments = useCallback(
    async (userId: string) => {
      return await storage.getManagerAssignments(userId);
    },
    [storage]
  );

  const updateManagerAssignments = useCallback(
    async (userId: string, managerIds: string[]) => {
      await storage.updateManagerAssignments(userId, managerIds);
    },
    [storage]
  );

  // ===== Vacation Adjustments =====

  const getVacationAdjustments = useCallback(
    async (userId: string, year: number) => {
      return await storage.getVacationAdjustments(userId, year);
    },
    [storage]
  );

  const addVacationAdjustment = useCallback(
    async (userId: string, year: number, data: { days: number; category: VacationAdjustmentCategory; reason: string }) => {
      return await storage.addVacationAdjustment(userId, year, data);
    },
    [storage]
  );

  const deleteVacationAdjustment = useCallback(
    async (userId: string, adjustmentId: string) => {
      await storage.deleteVacationAdjustment(userId, adjustmentId);
    },
    [storage]
  );

  const getMyTeam = useCallback(async () => {
    return await storage.getMyTeam();
  }, [storage]);

  const getTeamStats = useCallback(
    async (from: string, to: string) => {
      return await storage.getTeamStats(from, to);
    },
    [storage]
  );

  // ===== Org Chart =====

  const getOrgChart = useCallback(async () => {
    return await storage.getOrgChart();
  }, [storage]);

  // ===== Company Vacation =====

  const getCompanyVacationRule = useCallback(async () => {
    return await storage.getCompanyVacationRule();
  }, [storage]);

  const saveCompanyVacationRule = useCallback(
    async (rule: Omit<import('../types').CompanyVacationRule, 'id'>) => {
      return await storage.saveCompanyVacationRule(rule);
    },
    [storage]
  );

  // ===== Absence Types =====

  const getAbsenceTypes = useCallback(async () => {
    return await storage.getAbsenceTypes();
  }, [storage]);

  const getAllAbsenceTypes = useCallback(async () => {
    return await storage.getAllAbsenceTypes();
  }, [storage]);

  const createAbsenceType = useCallback(
    async (data: Omit<import('../types').AbsenceType, 'id' | 'isSystem' | 'systemKey' | 'active' | 'createdAt' | 'updatedAt'>) => {
      return await storage.createAbsenceType(data);
    },
    [storage]
  );

  const updateAbsenceType = useCallback(
    async (id: string, data: Partial<import('../types').AbsenceType>) => {
      return await storage.updateAbsenceType(id, data);
    },
    [storage]
  );

  const deleteAbsenceType = useCallback(
    async (id: string) => {
      return await storage.deleteAbsenceType(id);
    },
    [storage]
  );

  // ===== Employment History =====

  const getMySettingsSegments = useCallback(
    async (year: number) => {
      return await storage.getMySettingsSegments(year);
    },
    [storage]
  );

  const getUserSettingsSegments = useCallback(
    async (userId: string, year: number) => {
      return await storage.getUserSettingsSegments(userId, year);
    },
    [storage]
  );

  const getPayrollSegments = useCallback(
    async (from: string, to: string) => {
      return await storage.getPayrollSegments(from, to);
    },
    [storage]
  );

  const finalizeVacationHours = useCallback(
    async (userId: string, updates: Array<{ date: string; vacationCalculatedHours: number }>) => {
      return await storage.finalizeVacationHours(userId, updates);
    },
    [storage]
  );

  const getEmploymentHistory = useCallback(
    async (userId: string) => {
      return await storage.getEmploymentHistory(userId);
    },
    [storage]
  );

  const createHistoryRecord = useCallback(
    async (userId: string, data: CreateHistoryRequest) => {
      return await storage.createHistoryRecord(userId, data);
    },
    [storage]
  );

  const updateHistoryRecord = useCallback(
    async (userId: string, historyId: string, data: Partial<CreateHistoryRequest>) => {
      return await storage.updateHistoryRecord(userId, historyId, data);
    },
    [storage]
  );

  const deleteHistoryRecord = useCallback(
    async (userId: string, historyId: string) => {
      await storage.deleteHistoryRecord(userId, historyId);
    },
    [storage]
  );

  // ===== Auth =====

  const login = useCallback(
    async (msAccessToken: string) => {
      return await storage.login(msAccessToken);
    },
    [storage]
  );

  const loginSso = useCallback(
    async (ssoToken: string) => {
      return await storage.loginSso(ssoToken);
    },
    [storage]
  );

  const logout = useCallback(() => {
    storage.clearToken();
  }, [storage]);

  return {
    // Current User
    getMe,

    // Settings
    getSettings,
    saveSettings,
    getUserSettings,
    saveUserSettings,

    // Entries
    getEntries,
    saveEntries,
    getUserEntries,
    saveUserEntries,
    addManagerNote,

    // Carryover
    getCarryOver,
    saveCarryOver,
    getUserCarryOver,
    saveUserCarryOver,
    autoCalculateCarryover,

    // Users
    getAllUsers,
    getUsersByDepartment,
    getUserData,
    updateUserDepartment,
    updateUser,
    updateUserEmail,
    updatePersonnelNumber,
    createUser,
    searchAzureUsers,
    importAzureUser,
    deleteUser,

    // Manager Assignments
    getManagerAssignments,
    updateManagerAssignments,
    getMyTeam,
    getTeamStats,

    // Vacation Adjustments
    getVacationAdjustments,
    addVacationAdjustment,
    deleteVacationAdjustment,

    // Org Chart
    getOrgChart,

    // Approvals
    getApprovals,
    setApproval,

    // Company Vacation
    getCompanyVacationRule,
    saveCompanyVacationRule,

    // Absence Types
    getAbsenceTypes,
    getAllAbsenceTypes,
    createAbsenceType,
    updateAbsenceType,
    deleteAbsenceType,

    // Employment History
    getMySettingsSegments,
    getUserSettingsSegments,
    getPayrollSegments,
    finalizeVacationHours,
    getEmploymentHistory,
    createHistoryRecord,
    updateHistoryRecord,
    deleteHistoryRecord,

    // Auth
    login,
    loginSso,
    logout,

    // Storage Instance (falls direkt benötigt)
    storage,
  };
}
